import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { api, type SessionSummary, type TraceEvent } from '../api/client';
import { PipelineStrip, type PipelineStep } from '../components/PipelineStrip';
import { SessionStatsBar, type StatusCounts } from '../components/SessionStatsBar';
import {
  MagnifyingGlass,
  CaretLeft,
  CaretRight,
  TreeStructure,
  Funnel,
  Stack,
  Trash,
  ShieldCheck,
  Warning,
  CurrencyDollar,
} from '@phosphor-icons/react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  buildStepsFromEvents,
  deriveOverallStatus,
  formatDuration,
  formatToolName,
  getStatusStyle,
  getEventSummary,
  extractHttpStatus,
  extractDlpSummary,
  extractCostEstimate,
  extractDenyInfo,
  extractIncidentCount,
} from '../utils/trace-utils';

const PAGE_SIZE = 20;

const FILTER_CHIPS = [
  { label: 'status:allowed', key: 'status:allowed' },
  { label: 'status:blocked', key: 'status:blocked' },
  { label: 'status:approval', key: 'status:approval' },
  { label: 'tool:http.*', key: 'tool:http' },
  { label: 'calls:multi', key: 'calls:multi' },
];

/** A grouped trace = one row per unique task_id + tool_call_id (for flat events fallback) */
interface GroupedTrace {
  task_id: string;
  tool_call_id: string;
  tool_name: string;
  displayName: string;
  actor_id: string;
  timestamp: string;
  events: TraceEvent[];
  status: string;
  durationMs: number;
  steps: PipelineStep[];
  labels: string[];
}

/** Extract labels from the TOOL_CALL_RECEIVED event's metadata */
function extractLabels(events: TraceEvent[]): string[] {
  for (const e of events) {
    if (e.event_type === 'TOOL_CALL_RECEIVED') {
      const labels = e.metadata?.labels;
      if (Array.isArray(labels)) return labels as string[];
    }
  }
  return [];
}

function groupEventsByToolCall(events: TraceEvent[]): GroupedTrace[] {
  const groups = new Map<string, TraceEvent[]>();
  for (const e of events) {
    const groupKey = e.tool_call_id || e.task_id;
    if (!groups.has(groupKey)) groups.set(groupKey, []);
    groups.get(groupKey)!.push(e);
  }

  const traces: GroupedTrace[] = [];
  for (const [, evts] of groups) {
    evts.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    const first = evts[0];
    const last = evts[evts.length - 1];
    const durationMs = new Date(last.timestamp).getTime() - new Date(first.timestamp).getTime();
    const resultEvent = evts.find(e => e.event_type === 'TOOL_RESULT_RETURNED');
    const totalMs = (resultEvent?.metadata?.duration_ms as number) || durationMs;
    const rawToolName = first.tool_name || 'unknown';

    traces.push({
      task_id: first.task_id,
      tool_call_id: first.tool_call_id || first.task_id,
      tool_name: rawToolName,
      displayName: formatToolName(rawToolName, evts),
      actor_id: first.actor_id || '',
      timestamp: first.timestamp,
      events: evts,
      status: deriveOverallStatus(evts),
      durationMs: totalMs,
      steps: buildStepsFromEvents(evts),
      labels: extractLabels(evts),
    });
  }

  traces.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  return traces;
}

/** Unified row type: either a session summary or a legacy single-call trace */
interface DisplayRow {
  type: 'session' | 'single';
  id: string;
  timestamp: string;
  displayName: string;
  actor_id: string;
  status: string;
  durationMs: number;
  toolCallCount: number;
  toolsUsed: string[];
  steps?: PipelineStep[];
  statusCounts?: StatusCounts;
  labels: string[];
  // Rich metadata
  httpStatus?: number;
  dlpFindingsCount: number;
  redactionCount: number;
  estimatedCost?: number;
  denyRuleId?: string;
  denyReasons: string[];
  incidentCount: number;
  toolChips: string[]; // formatted tool names for multi-call
  // For sessions
  session?: SessionSummary;
  // For single-call (legacy)
  trace?: GroupedTrace;
}

function getStatusStyleForSession(status: string) {
  return getStatusStyle(status === 'blocked' ? 'denied' : status);
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDate(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function matchesFilter(row: DisplayRow, filter: string): boolean {
  if (filter.startsWith('tag:')) {
    const tagValue = filter.slice(4);
    return row.labels.includes(tagValue);
  }
  switch (filter) {
    case 'status:allowed':
      return row.status === 'ok';
    case 'status:blocked':
      return row.status === 'blocked' || row.status === 'denied' || row.status === 'error';
    case 'status:approval':
      return row.status === 'approval';
    case 'tool:http':
      return row.toolsUsed.some(t => t.toLowerCase().startsWith('http'));
    case 'calls:multi':
      return row.toolCallCount > 1;
    default:
      return true;
  }
}

/** Compact hover preview showing structured metadata for a row */
function HoverPreview({ row }: { row: DisplayRow }) {
  // Collect preview sections
  const sections: { label: string; content: string }[] = [];

  // Tools section
  if (row.type === 'session' && row.toolCallCount > 1) {
    const allTools = row.toolsUsed.map(t => formatToolName(t)).join(', ');
    sections.push({ label: 'Tools', content: allTools });
  }

  // For single-call traces, extract detailed event summaries
  if (row.trace) {
    const events = row.trace.events;
    // Policy
    const policyEvt = events.find(e => e.event_type === 'POLICY_DECIDED');
    if (policyEvt) {
      const entries = getEventSummary('POLICY_DECIDED', policyEvt.metadata);
      const parts = entries.map(e => `${e.label}: ${e.value}`).join(' · ');
      if (parts) sections.push({ label: 'Policy', content: parts });
    }
    // DLP
    const dlpEvents = events.filter(e => e.event_type === 'DLP_SCANNED');
    if (dlpEvents.length > 0) {
      const allEntries = dlpEvents.flatMap(e => getEventSummary('DLP_SCANNED', e.metadata));
      const types = allEntries.filter(e => e.label === 'Types').map(e => e.value);
      const redactions = allEntries.find(e => e.label === 'Redactions');
      const severity = allEntries.find(e => e.label === 'Severity');
      const parts: string[] = [];
      if (types.length > 0) parts.push(`Types: ${types.join(', ')}`);
      if (redactions) parts.push(`${redactions.value} redacted`);
      if (severity) parts.push(`Severity: ${severity.value}`);
      if (parts.length > 0) sections.push({ label: 'DLP', content: parts.join(' · ') });
    }
    // Budget
    const budgetEvt = events.find(e => e.event_type === 'BUDGET_CHECKED');
    if (budgetEvt) {
      const entries = getEventSummary('BUDGET_CHECKED', budgetEvt.metadata);
      const parts = entries.map(e => `${e.label}: ${e.value}`).join(' · ');
      if (parts) sections.push({ label: 'Budget', content: parts });
    }
    // Timing
    const resultEvt = events.find(e => e.event_type === 'TOOL_RESULT_RETURNED');
    if (resultEvt?.metadata?.step_timings) {
      const timings = resultEvt.metadata.step_timings as Record<string, number>;
      const parts = Object.entries(timings).map(([k, v]) => `${k} ${v}ms`).join(', ');
      sections.push({ label: 'Timing', content: parts });
    }
  } else if (row.session && row.toolCallCount > 1) {
    // Multi-call session aggregate summary
    const parts: string[] = [];
    if (row.estimatedCost !== undefined) parts.push(`Total cost: $${row.estimatedCost.toFixed(4)}`);
    if (row.redactionCount > 0) parts.push(`${row.redactionCount} total redactions`);
    if (row.dlpFindingsCount > 0) parts.push(`${row.dlpFindingsCount} DLP findings`);
    if (row.incidentCount > 0) parts.push(`${row.incidentCount} incidents`);
    if (row.denyReasons.length > 0) parts.push(`Deny reasons: ${row.denyReasons.join(', ')}`);
    if (parts.length > 0) sections.push({ label: 'Summary', content: parts.join(' · ') });
  }

  if (sections.length === 0) return null;

  return (
    <div
      className="px-4 pb-3"
      style={{ borderTop: '1px solid var(--border)' }}
    >
      <div
        className="grid gap-x-6 gap-y-1 pt-2"
        style={{ gridTemplateColumns: 'auto 1fr' }}
      >
        {sections.map((s) => (
          <div key={s.label} className="contents">
            <span className="text-[10px] font-medium" style={{ color: 'var(--text-tertiary)' }}>
              {s.label}
            </span>
            <span className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>
              {s.content}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Traces() {
  const { currentWorkspaceId: workspaceId } = useAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [rawEvents, setRawEvents] = useState<TraceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [confirmDeleteRow, setConfirmDeleteRow] = useState<DisplayRow | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleRowMouseEnter = useCallback((rowId: string) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoveredRowId(rowId), 300);
  }, []);

  const handleRowMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = null;
    setHoveredRowId(null);
  }, []);

  async function loadData() {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch sessions from new endpoint, and raw events as fallback for pipeline strips
      const [sessionsData, tracesData] = await Promise.all([
        api.getSessions(workspaceId, { limit: 200 }),
        api.getTraces(workspaceId, { limit: 200, offset: 0 }),
      ]);
      setSessions(sessionsData.sessions);
      setRawEvents(tracesData.traces);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load traces.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  // Group raw events by tool_call_id for pipeline strip computation
  const groupedTraces = useMemo(() => groupEventsByToolCall(rawEvents), [rawEvents]);

  // Build display rows from sessions
  const displayRows = useMemo((): DisplayRow[] => {
    // Map tool_call_ids to grouped traces for step computation
    const traceByToolCallId = new Map<string, GroupedTrace>();
    for (const t of groupedTraces) {
      traceByToolCallId.set(t.tool_call_id, t);
    }

    // Map session_ids to their grouped traces
    // When session_id is absent, the backend falls back to tool_call_id as session key,
    // so we also index grouped traces by tool_call_id to match them to sessions.
    const tracesBySessionId = new Map<string, GroupedTrace[]>();
    for (const t of groupedTraces) {
      const sessionId = t.events[0]?.session_id;
      if (sessionId) {
        if (!tracesBySessionId.has(sessionId)) tracesBySessionId.set(sessionId, []);
        tracesBySessionId.get(sessionId)!.push(t);
      }
      // Also index by tool_call_id for fallback matching (when session_id is absent,
      // the backend sessions endpoint uses tool_call_id as the session key)
      if (!tracesBySessionId.has(t.tool_call_id)) tracesBySessionId.set(t.tool_call_id, []);
      if (!tracesBySessionId.get(t.tool_call_id)!.includes(t)) {
        tracesBySessionId.get(t.tool_call_id)!.push(t);
      }
    }

    const rows: DisplayRow[] = [];
    const coveredToolCallIds = new Set<string>();

    for (const session of sessions) {
      const sessionTraces = tracesBySessionId.get(session.session_id) || [];

      if (session.tool_call_count === 1 && sessionTraces.length === 1) {
        // Single-call session: render as flat row
        const trace = sessionTraces[0];
        coveredToolCallIds.add(trace.tool_call_id);
        const dlp = extractDlpSummary(trace.events);
        const deny = extractDenyInfo(trace.events);
        rows.push({
          type: 'single',
          id: trace.task_id,
          timestamp: trace.timestamp,
          displayName: trace.displayName,
          actor_id: trace.actor_id,
          status: trace.status === 'denied' ? 'blocked' : trace.status,
          durationMs: trace.durationMs,
          toolCallCount: 1,
          toolsUsed: [trace.tool_name],
          steps: trace.steps,
          labels: trace.labels,
          httpStatus: extractHttpStatus(trace.events),
          dlpFindingsCount: dlp.findingsCount,
          redactionCount: dlp.redactionCount,
          estimatedCost: extractCostEstimate(trace.events),
          denyRuleId: deny?.ruleId,
          denyReasons: deny?.reasons || [],
          incidentCount: extractIncidentCount(trace.events),
          toolChips: [],
          trace,
        });
      } else {
        // Multi-call session: aggregate metadata across all traces
        for (const t of sessionTraces) coveredToolCallIds.add(t.tool_call_id);
        const allLabels = [...new Set(sessionTraces.flatMap(t => t.labels))];

        let totalDlpFindings = 0;
        let totalRedactions = 0;
        let totalCost = 0;
        let hasCost = false;
        let totalIncidents = 0;
        const allDenyReasons = new Set<string>();
        let firstDenyRuleId: string | undefined;
        for (const t of sessionTraces) {
          const dlp = extractDlpSummary(t.events);
          totalDlpFindings += dlp.findingsCount;
          totalRedactions += dlp.redactionCount;
          const cost = extractCostEstimate(t.events);
          if (cost !== undefined) { totalCost += cost; hasCost = true; }
          totalIncidents += extractIncidentCount(t.events);
          const deny = extractDenyInfo(t.events);
          if (deny) {
            if (!firstDenyRuleId && deny.ruleId) firstDenyRuleId = deny.ruleId;
            deny.reasons.forEach(r => allDenyReasons.add(r));
          }
        }

        // Build tool chips: up to 3 formatted tool names
        const uniqueTools = session.tools_used;
        const chips = uniqueTools.slice(0, 3).map(t => formatToolName(t));

        rows.push({
          type: 'session',
          id: session.session_id,
          timestamp: session.first_timestamp,
          displayName: session.tools_used.length === 1
            ? formatToolName(session.tools_used[0])
            : `${session.tool_call_count} tool calls`,
          actor_id: session.actor_id,
          status: session.overall_status,
          durationMs: session.duration_ms,
          toolCallCount: session.tool_call_count,
          toolsUsed: session.tools_used,
          statusCounts: session.status_counts,
          labels: allLabels,
          httpStatus: undefined, // not meaningful for multi-call
          dlpFindingsCount: totalDlpFindings,
          redactionCount: totalRedactions,
          estimatedCost: hasCost ? totalCost : undefined,
          denyRuleId: firstDenyRuleId,
          denyReasons: [...allDenyReasons],
          incidentCount: totalIncidents,
          toolChips: chips,
          session,
        });
      }
    }

    // Add any ungrouped traces that aren't in a session (backward compat for old events)
    for (const trace of groupedTraces) {
      if (coveredToolCallIds.has(trace.tool_call_id)) continue;
      const dlp = extractDlpSummary(trace.events);
      const deny = extractDenyInfo(trace.events);
      rows.push({
        type: 'single',
        id: trace.task_id,
        timestamp: trace.timestamp,
        displayName: trace.displayName,
        actor_id: trace.actor_id,
        status: trace.status === 'denied' ? 'blocked' : trace.status,
        durationMs: trace.durationMs,
        toolCallCount: 1,
        toolsUsed: [trace.tool_name],
        steps: trace.steps,
        labels: trace.labels,
        httpStatus: extractHttpStatus(trace.events),
        dlpFindingsCount: dlp.findingsCount,
        redactionCount: dlp.redactionCount,
        estimatedCost: extractCostEstimate(trace.events),
        denyRuleId: deny?.ruleId,
        denyReasons: deny?.reasons || [],
        incidentCount: extractIncidentCount(trace.events),
        toolChips: [],
        trace,
      });
    }

    rows.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
    return rows;
  }, [sessions, groupedTraces]);

  const filteredRows = useMemo(() => {
    let result = displayRows;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.id.toLowerCase().includes(q) ||
          r.displayName.toLowerCase().includes(q) ||
          r.actor_id.toLowerCase().includes(q) ||
          r.status.toLowerCase().includes(q) ||
          r.toolsUsed.some(t => t.toLowerCase().includes(q)) ||
          r.labels.some((l) => l.toLowerCase().includes(q)),
      );
    }

    if (activeFilters.size > 0) {
      result = result.filter((r) => {
        for (const f of activeFilters) {
          if (matchesFilter(r, f)) return true;
        }
        return false;
      });
    }

    return result;
  }, [displayRows, search, activeFilters]);

  const pagedRows = filteredRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE);
  const currentPage = page + 1;

  function toggleFilter(key: string) {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleRowClick(row: DisplayRow) {
    if (row.type === 'session' && row.toolCallCount > 1) {
      navigate(`/sessions/${row.id}`);
    } else {
      navigate(`/traces/${row.id}`);
    }
  }

  async function handleClearAll() {
    if (!workspaceId) return;
    try {
      await api.deleteAllTraces(workspaceId);
      setConfirmClearAll(false);
      setDeleteMessage('All traces cleared');
      setTimeout(() => setDeleteMessage(null), 3000);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear traces.');
      setConfirmClearAll(false);
    }
  }

  async function handleDeleteRow(row: DisplayRow) {
    if (!workspaceId) return;
    try {
      if (row.type === 'session') {
        await api.deleteSession(workspaceId, row.id);
      } else {
        await api.deleteTrace(workspaceId, row.id);
      }
      setConfirmDeleteRow(null);
      setDeleteMessage('Trace deleted');
      setTimeout(() => setDeleteMessage(null), 3000);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete trace.');
      setConfirmDeleteRow(null);
    }
  }

  if (!workspaceId) {
    return (
      <div className="card p-8 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
        No workspace found. Please complete onboarding first.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg"
            style={{ background: 'var(--color-teal-muted)' }}
          >
            <TreeStructure size={22} weight="duotone" style={{ color: 'var(--accent-teal)' }} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
              Traces
            </h1>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Inspect pipeline execution for every tool call
            </p>
          </div>
        </div>
        {displayRows.length > 0 && (
          <button
            onClick={() => setConfirmClearAll(true)}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
            style={{
              background: 'rgba(248,113,113,0.1)',
              border: '1px solid rgba(248,113,113,0.25)',
              color: 'var(--color-error)',
            }}
          >
            <Trash size={14} weight="duotone" />
            Clear All
          </button>
        )}
      </div>

      {/* Success toast */}
      {deleteMessage && (
        <div
          className="rounded-lg px-4 py-3 text-sm"
          style={{
            background: 'var(--color-teal-muted)',
            border: '1px solid var(--accent-teal)',
            color: 'var(--accent-teal)',
          }}
        >
          {deleteMessage}
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          className="rounded-lg px-4 py-3 text-sm"
          style={{
            background: 'var(--color-error-muted)',
            border: '1px solid rgba(248,113,113,0.25)',
            color: 'var(--color-error)',
          }}
        >
          {error}
        </div>
      )}

      {/* Search + Filter Bar */}
      <div className="space-y-3">
        <div className="relative">
          <MagnifyingGlass
            size={16}
            weight="duotone"
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-tertiary)' }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by session ID, tool name, actor..."
            className="w-full rounded-lg py-2.5 pl-9 pr-4 text-sm outline-none transition-colors"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--text-primary)',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent-teal)')}
            onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
          />
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <Funnel size={14} weight="duotone" style={{ color: 'var(--text-tertiary)' }} />
          {FILTER_CHIPS.map((chip) => {
            const isActive = activeFilters.has(chip.key);
            return (
              <button
                key={chip.key}
                onClick={() => toggleFilter(chip.key)}
                className="rounded-md px-2.5 py-1 text-xs font-medium transition-all"
                style={{
                  background: isActive ? 'var(--color-teal-muted)' : 'var(--surface-2)',
                  border: `1px solid ${isActive ? 'var(--accent-teal)' : 'var(--border)'}`,
                  color: isActive ? 'var(--accent-teal)' : 'var(--text-secondary)',
                }}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Trace List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="spinner-gradient h-8 w-8" />
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="card p-12 text-center">
          <TreeStructure
            size={40}
            weight="duotone"
            className="mx-auto mb-3"
            style={{ color: 'var(--text-tertiary)' }}
          />
          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            {search.trim() || activeFilters.size > 0
              ? 'No traces match the current filters'
              : 'No traces recorded yet'}
          </p>
          <p className="mt-1 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            {search.trim() || activeFilters.size > 0
              ? 'Try adjusting your search or clearing filters.'
              : 'Execute a tool call through the gateway to see traces here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {pagedRows.map((row) => {
            const statusStyle = getStatusStyleForSession(row.status);
            const isMulti = row.toolCallCount > 1;
            const isHovered = hoveredRowId === row.id;
            const hasBadges = row.actor_id || row.httpStatus !== undefined || row.redactionCount > 0 || row.estimatedCost !== undefined || row.denyRuleId || row.incidentCount > 0;
            return (
              <div
                key={row.id}
                className="rounded-lg transition-all"
                style={{
                  background: 'var(--surface-1)',
                  border: '1px solid var(--border)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--surface-2)';
                  e.currentTarget.style.borderColor = 'var(--border-subtle)';
                  handleRowMouseEnter(row.id);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--surface-1)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                  handleRowMouseLeave();
                }}
              >
                <button
                  onClick={() => handleRowClick(row)}
                  className="flex w-full items-center gap-4 px-4 py-3.5 text-left"
                >
                  {/* Left: timestamp + tool + actor + badges */}
                  <div className="w-56 shrink-0 space-y-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>
                        {formatDate(row.timestamp)}
                      </span>
                      <span className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                        {formatTimestamp(row.timestamp)}
                      </span>
                    </div>
                    <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                      {row.displayName}
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-mono truncate" style={{ color: 'var(--text-tertiary)' }} title={row.id}>
                        {row.id}
                      </span>
                      {row.durationMs > 0 && (
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          {formatDuration(row.durationMs)}
                        </span>
                      )}
                    </div>
                    {/* Actor ID */}
                    {row.actor_id && (
                      <div className="text-[10px] font-mono truncate" style={{ color: 'var(--text-tertiary)' }} title={row.actor_id}>
                        {row.actor_id}
                      </div>
                    )}
                    {/* Multi-call badge */}
                    {isMulti && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Stack size={12} weight="duotone" style={{ color: 'var(--accent-violet)' }} />
                        <span
                          className="text-[10px] font-medium"
                          style={{ color: 'var(--accent-violet)' }}
                        >
                          {row.toolCallCount} tool calls
                        </span>
                        {row.toolsUsed.length > 1 && (
                          <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                            ({row.toolsUsed.length} tools)
                          </span>
                        )}
                      </div>
                    )}
                    {/* Inline metadata badges */}
                    {hasBadges && (
                      <div className="flex flex-wrap items-center gap-1 mt-0.5">
                        {row.httpStatus !== undefined && (
                          <span
                            className="rounded px-1.5 py-0 text-[10px] font-medium font-mono"
                            style={{
                              background: row.httpStatus < 300 ? 'rgba(45,212,191,0.12)' : row.httpStatus < 400 ? 'rgba(250,204,21,0.12)' : 'rgba(248,113,113,0.12)',
                              color: row.httpStatus < 300 ? 'var(--accent-teal)' : row.httpStatus < 400 ? 'var(--color-warning)' : 'var(--color-error)',
                            }}
                          >
                            {row.httpStatus}
                          </span>
                        )}
                        {row.redactionCount > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 rounded px-1.5 py-0 text-[10px] font-medium"
                            style={{ background: 'rgba(139,92,246,0.12)', color: 'var(--accent-violet)' }}
                          >
                            <ShieldCheck size={10} weight="duotone" />
                            {row.redactionCount} redacted
                          </span>
                        )}
                        {row.estimatedCost !== undefined && (
                          <span
                            className="inline-flex items-center gap-0.5 rounded px-1.5 py-0 text-[10px] font-medium font-mono"
                            style={{ background: 'rgba(45,212,191,0.12)', color: 'var(--accent-teal)' }}
                          >
                            ${row.estimatedCost.toFixed(4)}
                          </span>
                        )}
                        {row.denyRuleId && (
                          <span
                            className="rounded px-1.5 py-0 text-[10px] font-medium"
                            style={{ background: 'rgba(248,113,113,0.12)', color: 'var(--color-error)' }}
                          >
                            {row.denyRuleId}
                          </span>
                        )}
                        {row.incidentCount > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 rounded px-1.5 py-0 text-[10px] font-medium"
                            style={{ background: 'rgba(248,113,113,0.12)', color: 'var(--color-error)' }}
                          >
                            <Warning size={10} weight="duotone" />
                            {row.incidentCount}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Tool chips for multi-call sessions */}
                    {isMulti && row.toolChips.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {row.toolChips.map((chip, i) => (
                          <span
                            key={i}
                            className="rounded px-1.5 py-0 text-[10px] font-medium"
                            style={{
                              background: 'rgba(139,92,246,0.12)',
                              color: 'var(--accent-violet)',
                            }}
                          >
                            {chip}
                          </span>
                        ))}
                        {row.toolsUsed.length > 3 && (
                          <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                            +{row.toolsUsed.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                    {row.labels.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {row.labels.slice(0, 3).map((label) => (
                          <span
                            key={label}
                            className="rounded px-1.5 py-0 text-[10px] font-medium"
                            style={{
                              background: 'rgba(139,92,246,0.12)',
                              color: 'var(--accent-violet)',
                            }}
                          >
                            {label}
                          </span>
                        ))}
                        {row.labels.length > 3 && (
                          <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                            +{row.labels.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Center: PipelineStrip for single calls, stats bar for sessions */}
                  <div className="flex-1 flex items-center justify-center min-w-0">
                    {isMulti && row.statusCounts ? (
                      <SessionStatsBar counts={row.statusCounts} total={row.toolCallCount} />
                    ) : row.steps && row.steps.length > 0 ? (
                      <PipelineStrip steps={row.steps} size="sm" />
                    ) : null}
                  </div>

                  {/* Right: status badge + delete */}
                  <div className="w-36 shrink-0 flex items-center justify-end gap-2">
                    <span
                      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                      style={{
                        background: statusStyle.bg,
                        color: statusStyle.text,
                      }}
                    >
                      {statusStyle.label}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteRow(row);
                      }}
                      className="rounded p-1 transition-colors"
                      style={{ color: 'var(--text-tertiary)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--color-error)';
                        e.currentTarget.style.background = 'rgba(248,113,113,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--text-tertiary)';
                        e.currentTarget.style.background = 'transparent';
                      }}
                      title="Delete trace"
                    >
                      <Trash size={14} weight="duotone" />
                    </button>
                  </div>
                </button>

                {/* Hover preview — expandable detail section */}
                <div
                  style={{
                    maxHeight: isHovered ? '300px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.2s ease-in-out',
                  }}
                >
                  <HoverPreview row={row} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Page {currentPage} of {totalPages}
            <span className="ml-1" style={{ color: 'var(--text-tertiary)' }}>
              ({filteredRows.length} traces)
            </span>
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-30"
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
              }}
            >
              <CaretLeft size={14} weight="bold" />
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={currentPage >= totalPages}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-30"
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
              }}
            >
              Next
              <CaretRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={confirmClearAll}
        title="Clear All Traces"
        message="This will permanently delete all traces. Dashboard stats will reset. This action cannot be undone."
        confirmLabel="Clear All"
        confirmVariant="danger"
        onConfirm={handleClearAll}
        onCancel={() => setConfirmClearAll(false)}
      />
      <ConfirmDialog
        open={!!confirmDeleteRow}
        title="Delete Trace"
        message={`Delete ${confirmDeleteRow?.type === 'session' ? 'session' : 'trace'} "${confirmDeleteRow?.id.slice(0, 12)}..."? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={() => confirmDeleteRow && handleDeleteRow(confirmDeleteRow)}
        onCancel={() => setConfirmDeleteRow(null)}
      />
    </div>
  );
}
